import {
  Timestamp,
  type DocumentData,
  type DocumentSnapshot,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import type {
  Company,
  FieldCheckInMessageData,
  Member,
  Task,
  TaskMessage,
} from './models';
import { roleFromFirestore } from './models';
import { clampChatMessageWindowMinutes } from './chatMessageWindow';
import { parseTaskAppreciations } from './taskAppreciation';
import { normalizeQuickFilterIds } from './taskListFilters';

function toDateOrUndefined(v: unknown): Date | undefined {
  if (v == null) return undefined;
  return toDate(v);
}

function toNumber(v: unknown): number {
  if (typeof v === 'number') return v;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function toDate(v: unknown): Date {
  if (!v) return new Date();
  if (v instanceof Timestamp) return v.toDate();
  if (v instanceof Date) return v;
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return (v as Timestamp).toDate();
  }
  const p = Date.parse(String(v));
  return Number.isNaN(p) ? new Date() : new Date(p);
}

function stringList(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.map((e) => String(e)).filter(Boolean);
}

function parseTags(data: DocumentData): string[] {
  const tagsRaw = data.tags;
  if (Array.isArray(tagsRaw)) {
    return tagsRaw.map((e) => String(e).trim()).filter(Boolean);
  }
  const tagStr = data.tag as string | undefined;
  if (tagStr && tagStr.trim()) return [tagStr.trim()];
  return [];
}

export function taskFromSnapshot(
  snap: QueryDocumentSnapshot | DocumentSnapshot,
  companyId: string,
): Task {
  const data = (snap.data({ serverTimestamps: 'estimate' }) ??
    {}) as DocumentData;
  return {
    id: snap.id,
    companyId,
    title: (data.title as string) ?? '',
    description: data.description as string | undefined,
    tags: parseTags(data),
    createdBy: (data.createdBy as string) ?? '',
    createdAt: toDate(data.createdAt),
    lastMessageAt:
      data.lastMessageAt != null ? toDate(data.lastMessageAt) : undefined,
    eta: data.eta != null ? toDate(data.eta) : undefined,
    repeat: data.repeat as string | undefined,
    repeatTime: data.repeatTime as string | undefined,
    repeatEtaTime: data.repeatEtaTime as string | undefined,
    taskNumber:
      typeof data.taskNumber === 'number' ? data.taskNumber : undefined,
    taskDisplayCode: data.taskDisplayCode as string | undefined,
    status: data.status === 'closed' ? 'closed' : 'open',
    closedBy: data.closedBy as string | undefined,
    closedAt: data.closedAt != null ? toDate(data.closedAt) : undefined,
    assignees: stringList(data.assignees),
    primaryAssigneeId: (() => {
      const raw = data.primaryAssigneeId;
      if (typeof raw === 'string') {
        const t = raw.trim();
        return t === '' ? undefined : t;
      }
      return undefined;
    })(),
    supervisors: stringList(data.supervisors),
    hasClosureRequest: data.closureRequest != null,
    closureRequestedAt: (() => {
      const cr = data.closureRequest;
      if (cr == null || typeof cr !== 'object') return undefined;
      return toDateOrUndefined((cr as DocumentData).requestedAt);
    })(),
    lastClosureRequestedAt: (() => {
      const lr = data.lastClosureRequest;
      if (lr == null || typeof lr !== 'object') return undefined;
      return toDateOrUndefined((lr as DocumentData).requestedAt);
    })(),
    lastClosureRequestStatus: (() => {
      const lr = data.lastClosureRequest;
      if (lr == null || typeof lr !== 'object') return undefined;
      const s = (lr as DocumentData).status;
      return typeof s === 'string' && s.trim() ? s.trim() : undefined;
    })(),
    closedWithoutClosureRequest: data.closedWithoutClosureRequest === true,
    closedAsNotDone: data.closedAsNotDone === true,
    isBlocked: data.taskBlock != null,
    isEtaIncreaseRequested: data.etaIncreaseRequest != null,
    appreciations: parseTaskAppreciations(data as Record<string, unknown>),
    deletedAt: data.deletedAt != null ? toDate(data.deletedAt) : undefined,
    deletedBy: typeof data.deletedBy === 'string' ? data.deletedBy : undefined,
    mentionedUserIds: stringList(data.mentionedUserIds),
  };
}

export function companyFromSnapshot(
  doc: QueryDocumentSnapshot | DocumentSnapshot,
  backgroundOverride?: string,
): Company {
  const data = doc.data() as DocumentData;
  const mins = Number(data.chatMessageDeleteWindowMinutes ?? 30);
  const clampedMins = clampChatMessageWindowMinutes(mins);
  const editMins = Number(data.chatMessageEditWindowMinutes ?? clampedMins);
  const clampedEditMins = clampChatMessageWindowMinutes(editMins);
  return {
    id: doc.id,
    name: (data.name as string) ?? '',
    businessName: data.businessName as string | undefined,
    colorHex:
      typeof data.colorHex === 'number'
        ? data.colorHex
        : Number(data.colorHex) || 0xff3c3c43,
    joinCode: data.joinCode as string | undefined,
    logoUrl: data.logoUrl as string | undefined,
    description: data.description as string | undefined,
    backgroundAsset: backgroundOverride ?? (data.backgroundAsset as string | undefined),
    autoSelectSupervisorsOnNewTasks:
      data.autoSelectSupervisorsOnNewTasks !== false,
    allowChatMessageDelete: data.allowChatMessageDelete === true,
    allowChatMessageEdit:
      data.allowChatMessageEdit === true ||
      (data.allowChatMessageEdit == null && data.allowChatMessageDelete === true),
    chatMessageDeleteWindowMinutes: clampedMins,
    chatMessageEditWindowMinutes: clampedEditMins,
    quickFilterIds: normalizeQuickFilterIds(data.quickFilterIds),
    billingPlan: data.billingPlan as string | undefined,
    premiumComplimentaryEndsAt: toDateOrUndefined(data.premiumComplimentaryEndsAt),
    premiumSubscriptionPaidThrough: toDateOrUndefined(
      data.premiumSubscriptionPaidThrough,
    ),
    createdAt: toDate(data.createdAt),
    createdBy: (data.createdBy as string) ?? '',
  };
}

export function memberFromSnapshot(
  uid: string,
  data: DocumentData,
): Member {
  return {
    uid,
    role: roleFromFirestore(data.role as string),
    displayName: data.displayName as string | undefined,
    phoneNumber: data.phoneNumber as string | undefined,
    joinedAt: toDate(data.joinedAt),
    backgroundAsset: data.backgroundAsset as string | undefined,
  };
}

function fieldCheckInFromMap(raw: unknown): FieldCheckInMessageData | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const map = raw as Record<string, unknown>;
  const checkInId = (map.checkInId as string) ?? '';
  const sessionId = (map.sessionId as string) ?? '';
  if (!checkInId || !sessionId) return undefined;
  const photoUrls = Array.isArray(map.photoUrls)
    ? map.photoUrls.filter((u): u is string => typeof u === 'string')
    : [];
  return {
    checkInId,
    sessionId,
    photoUrls,
    lat: toNumber(map.lat),
    lng: toNumber(map.lng),
    userName: map.userName as string | undefined,
  };
}

export function messageFromSnapshot(
  doc: QueryDocumentSnapshot | DocumentSnapshot,
): TaskMessage {
  const data = doc.data() as DocumentData;
  return {
    id: doc.id,
    senderId: (data.senderId as string) ?? '',
    text: (data.text as string) ?? '',
    createdAt: toDate(data.createdAt),
    type: (data.type as string) ?? 'user',
    attachmentName: data.attachmentName as string | undefined,
    textFormat:
      data.textFormat === 'markdown' ? 'markdown' : undefined,
    fieldCheckIn: fieldCheckInFromMap(data.fieldCheckIn),
    editedAt: toDateOrUndefined(data.editedAt),
    deletedAt: toDateOrUndefined(data.deletedAt),
    mentionedUserIds: stringList(data.mentionedUserIds),
  };
}
